import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import {
  Shield, Building2, Users, Globe, Plus, Copy, CheckCircle2,
  Pencil, Trash2, KeyRound, UserPlus, ChevronRight, PauseCircle, PlayCircle, ChevronDown, Upload,
} from "lucide-react";
import { useSiteContext } from "../../components/layout/Layout";
import { toast } from "sonner";

// Mirrors PLAN_LIMITS in backend/tenant.py.
const PLAN_OPTIONS = [
  { value: "basic", label: "Basic · 1 site" },
  { value: "pro", label: "Pro · 10 sites" },
  { value: "enterprise", label: "Enterprise · unlimited" },
];

// ── Type-to-confirm danger dialog ────────────────────────────────────────────
function ConfirmSlugDialog({ title, description, slug, confirmLabel, onConfirm, onClose, danger = true }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const match = typed.trim() === slug;

  const run = async () => {
    if (!match) return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm">
        <DialogHeader>
          <DialogTitle className={danger ? "text-red-600" : "text-amber-600"}>{title}</DialogTitle>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Type <span className="font-mono font-bold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded">{slug}</span> to confirm:
          </p>
          <Input
            autoFocus
            className="bg-gray-50 border-gray-200 font-mono"
            placeholder={slug}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && match && run()}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={run}
            disabled={!match || busy}
            className={danger
              ? "bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 disabled:opacity-30"
              : "bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 disabled:opacity-30"}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── KPI card ────────────────────────────────────────────────────────────────
function Kpi({ label, value, icon: Icon, tone }) {
  return (
    <div className="glass rounded-lg p-5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
        <Icon className={`w-4 h-4 ${tone || "text-orange-400"}`} />
      </div>
      <div className="text-3xl font-extrabold mt-2">{value}</div>
    </div>
  );
}

// ── Badge colour maps ────────────────────────────────────────────────────────
const TIER_COLORS = {
  enterprise: "bg-purple-50 text-purple-700 border-purple-200",
  pro:        "bg-sky-50 text-sky-700 border-sky-200",
  basic:      "bg-gray-50 text-gray-700 border-gray-200",
};
// Client logo shown in the sidebar to this organisation's own users. Downsized in the browser to
// fit 480×160 (PNG, so transparency survives) before upload; the backend caps size and type.
const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
function fitLogo(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 480 / img.width, 160 / img.height);
        const c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(img.width * scale));
        c.height = Math.max(1, Math.round(img.height * scale));
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function OrgLogoField({ org, onChanged }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) { toast.error("Logo must be PNG, JPEG or WebP"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)"); return; }
    setBusy(true);
    try {
      const dataUrl = await fitLogo(file);
      await api.post(`/admin/orgs/${org.org_id}/logo`, { logo_url: dataUrl });
      toast.success("Logo updated");
      onChanged(dataUrl);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Logo upload failed");
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this organisation's logo?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/orgs/${org.org_id}/logo`);
      toast.success("Logo removed");
      onChanged(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove logo");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="flex items-center gap-3">
        <div className="w-40 h-14 rounded-md border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
          {org.logo_url
            ? <img src={org.logo_url} alt={`${org.name} logo`} className="max-h-12 max-w-[152px] object-contain" />
            : <span className="text-[11px] text-gray-400">No logo</span>}
        </div>
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex gap-1.5">
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="h-8 border-gray-300 text-gray-700" data-testid="org-logo-upload">
              <Upload className="w-3.5 h-3.5 mr-1" /> {busy ? "Saving…" : org.logo_url ? "Replace" : "Upload"}
            </Button>
            {org.logo_url && (
              <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove} className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50">
                Remove
              </Button>
            )}
          </div>
          <p className="text-[10px] text-gray-500 leading-snug">PNG, JPEG or WebP · shown in the sidebar for this organisation's users</p>
        </div>
      </div>
      <input ref={inputRef} type="file" accept={LOGO_TYPES.join(",")} className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

const STATUS_COLORS = {
  active:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  trial:     "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  suspended: "bg-red-50 text-red-700 border-red-200",
  deleted:   "bg-gray-50 text-gray-600 border-gray-200",
};
const USER_STATUS_COLORS = {
  active:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  suspended: "bg-red-50 text-red-700 border-red-200",
};
const ROLE_LABELS = {
  customer_admin:     "Org Admin",
  admin:              "Admin",
  site_manager:       "Site Manager",
  building_manager:   "Building Manager",
  fire_safety_officer:"Safety Officer",
  viewer:             "Viewer",
};
const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([v, l]) => ({ value: v, label: l }));

// ── Slug helper ──────────────────────────────────────────────────────────────
function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

// ── Create org form defaults ─────────────────────────────────────────────────
const EMPTY_CREATE = { org_name: "", org_slug: "", admin_name: "", admin_email: "", admin_password: "", phone: "" };

// ── Inline field ─────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-gray-600 uppercase tracking-wider">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ── Reset Password mini-dialog ───────────────────────────────────────────────
function ResetPasswordDialog({ orgId, target, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (pw.length < 8) { toast.error("Minimum 8 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/orgs/${orgId}/users/${target.user_id}/reset-password`, { password: pw });
      toast.success(`Password updated for ${target.email}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <p className="text-xs text-gray-600 mt-1 font-mono">{target.email}</p>
        </DialogHeader>
        <Field label="New Password">
          <Input type="text" className="bg-gray-50 border-gray-200 font-mono"
            value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Minimum 8 characters" />
        </Field>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="f360-gradient border-0">
            {saving ? "Saving…" : "Set Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Add User mini-dialog ─────────────────────────────────────────────────────
function AddUserDialog({ orgId, onClose, onCreated }) {
  const EMPTY = { name: "", email: "", password: "", role: "viewer", phone: "" };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name || !form.email || !form.password || !form.phone.trim()) {
      toast.error("Name, email, mobile number and password are required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/admin/orgs/${orgId}/users`, form);
      toast.success(`User ${r.data.email} created`);
      onCreated(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
        <DialogHeader><DialogTitle>Add User to Organisation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full Name *">
              <Input className="bg-gray-50 border-gray-200" value={form.name} onChange={f("name")} placeholder="John Doe" />
            </Field>
            <Field label="Mobile number *">
              <Input type="tel" inputMode="tel" className="bg-gray-50 border-gray-200" value={form.phone} onChange={f("phone")} placeholder="98200 12345" />
            </Field>
          </div>
          <Field label="Email *">
            <Input type="email" className="bg-gray-50 border-gray-200" value={form.email} onChange={f("email")} placeholder="user@org.com" />
          </Field>
          <Field label="Initial Password *">
            <Input type="text" className="bg-gray-50 border-gray-200 font-mono" value={form.password} onChange={f("password")} placeholder="Minimum 8 characters" />
          </Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
              <SelectTrigger className="bg-gray-50 border-gray-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="f360-gradient border-0">
            {saving ? "Creating…" : "Create User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit User row ────────────────────────────────────────────────────────────
function UserRow({ orgId, u, onUpdated, onDeleted, onResetPw, onSuspendToggle }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: u.name, email: u.email, role: u.role, phone: u.phone || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/orgs/${orgId}/users/${u.user_id}`, form);
      toast.success("User updated");
      onUpdated({ ...u, ...form });
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const doDelete = async () => {
    await api.delete(`/admin/orgs/${orgId}/users/${u.user_id}`);
    toast.success("User deleted");
    onDeleted(u.user_id);
  };

  if (editing) {
    return (
      <tr className="border-t border-gray-200 bg-gray-50">
        <td className="px-4 py-2">
          <Input className="bg-gray-50 border-gray-200 h-8 text-sm" value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
        </td>
        <td className="px-4 py-2">
          <Input type="email" className="bg-gray-50 border-gray-200 h-8 text-sm" value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          <Input type="tel" inputMode="tel" className="bg-gray-50 border-gray-200 h-8 text-sm mt-1" value={form.phone} placeholder="Mobile number"
            onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
        </td>
        <td className="px-4 py-2">
          <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
            <SelectTrigger className="bg-gray-50 border-gray-200 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900">
              {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </td>
        <td className="px-4 py-2 text-right">
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="f360-gradient border-0 h-7 text-xs">
              {saving ? "…" : "Save"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs">Cancel</Button>
          </div>
        </td>
      </tr>
    );
  }

  const isSuspended = u.status === "suspended";

  return (
    <tr className={`border-t border-gray-200 hover:bg-gray-50 transition-colors ${isSuspended ? "opacity-60" : ""}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${isSuspended ? "bg-gray-500" : "f360-gradient"}`}>
            {u.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div>
            <div className="text-sm font-medium">{u.name}</div>
            {isSuspended && <div className="text-[10px] text-red-600 font-bold uppercase">Suspended</div>}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
        {u.email}
        {u.phone ? <div className="text-[11px] text-gray-400">{u.phone}</div> : <div className="text-[11px] text-amber-600 font-sans">No mobile number</div>}
      </td>
      <td className="px-4 py-3">
        <Badge className="border text-[10px] uppercase tracking-widest bg-sky-50 text-sky-700 border-sky-200">
          {ROLE_LABELS[u.role] || u.role}
        </Badge>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="h-7 w-7 p-0" title="Edit">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onResetPw(u)} className="h-7 w-7 p-0" title="Reset password">
            <KeyRound className="w-3.5 h-3.5 text-amber-600" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onSuspendToggle(u, isSuspended ? "activate" : "suspend")}
            className={`h-7 w-7 p-0 ${isSuspended ? "hover:text-emerald-600" : "hover:text-amber-600"}`}
            title={isSuspended ? "Reactivate user" : "Suspend user"}>
            {isSuspended ? <PlayCircle className="w-3.5 h-3.5 text-emerald-600" /> : <PauseCircle className="w-3.5 h-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(true)} className="h-7 w-7 p-0 hover:text-red-600" title="Delete user">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
      {deleteConfirm && (
        <ConfirmSlugDialog
          danger
          title="Delete User"
          description={`${u.email} loses access immediately and leaves the user list. Their history (alerts, audit log) is kept, and the email can be invited again.`}
          slug={u.email}
          confirmLabel="Delete User"
          onConfirm={doDelete}
          onClose={() => setDeleteConfirm(false)}
        />
      )}
    </tr>
  );
}

// ── Edit Org Drawer ──────────────────────────────────────────────────────────
// mobile_numbers has been stored three ways over time: unset, a JSON string ('["+91…"]') and —
// since the org-details save started persisting it (2026-09-25) — a real list. JSON.parse() on a
// list throws, which crashed this drawer the moment it opened ("Edit does nothing").
function parseMobileNumbers(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    try { const parsed = JSON.parse(v); return Array.isArray(parsed) ? parsed : [String(parsed)]; }
    catch { return v.split(",").map((m) => m.trim()).filter(Boolean); }
  }
  return [];
}

function EditOrgDrawer({ org, onClose, onOrgUpdated, onOrgDeleted, onOrgPurged }) {
  // Re-read the signed-in user's own org after a logo change, so the sidebar picks it up.
  const { refreshMyOrg } = useSiteContext();
  const [details, setDetails] = useState({
    name: org.name || "",
    slug: org.slug || "",
    phone: org.phone || "",
    mobile_numbers: parseMobileNumbers(org.mobile_numbers).join(", "),
    gst_number: org.gst_number || "",
    gst_state: org.gst_state || "",
    billing_address: org.billing_address || "",
    billing_city: org.billing_city || "",
    billing_state: org.billing_state || "",
    billing_pin: org.billing_pin || "",
    shipping_address: org.shipping_address || "",
    shipping_city: org.shipping_city || "",
    shipping_state: org.shipping_state || "",
    shipping_pin: org.shipping_pin || "",
    billing_email: org.billing_email || "",
    billing_contact_person: org.billing_contact_person || "",
  });
  const [savingDetails, setSavingDetails] = useState(false);
  const [orgStatus, setOrgStatus] = useState(org.status || "active");

  const [users, setUsers] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { type: "suspend"|"delete" }

  // Load users when drawer opens
  useEffect(() => {
    api.get(`/admin/orgs/${org.org_id}`)
      .then((r) => setUsers(r.data.users || []))
      .catch(() => setUsers([]));
  }, [org.org_id]);

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      const payload = {
        ...details,
        mobile_numbers: details.mobile_numbers
          .split(",")
          .map((m) => m.trim())
          .filter((m) => m),
      };
      await api.put(`/admin/orgs/${org.org_id}`, payload);
      toast.success("Organisation updated");
      onOrgUpdated({ ...org, ...payload });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSavingDetails(false);
    }
  };

  const doSuspendToggle = async () => {
    const action = orgStatus === "suspended" ? "activate" : "suspend";
    const r = await api.post(`/admin/orgs/${org.org_id}/suspend`, { action });
    setOrgStatus(r.data.status);
    onOrgUpdated({ ...org, ...details, status: r.data.status });
    toast.success(`Organisation ${r.data.status}`);
    setConfirmDialog(null);
  };

  const doDeleteOrg = async () => {
    await api.delete(`/admin/orgs/${org.org_id}`);
    toast.success("Organisation deleted");
    onOrgDeleted(org.org_id);
    onClose();
  };

  const doPurgeOrg = async () => {
    await api.delete(`/admin/orgs/${org.org_id}/purge`);
    toast.success("Organisation permanently deleted");
    onOrgPurged(org.org_id);
    onClose();
  };

  const suspendUser = async (u, action) => {
    try {
      const r = await api.post(`/admin/orgs/${org.org_id}/users/${u.user_id}/suspend`, { action });
      setUsers((prev) => prev.map((x) => x.user_id === u.user_id ? { ...x, status: r.data.status } : x));
      toast.success(`User ${r.data.status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <Sheet open onOpenChange={onClose}>
        <SheetContent className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-orange-400" />
              {org.name}
            </SheetTitle>
            <p className="text-xs text-gray-500 font-mono">{org.org_id}</p>
          </SheetHeader>

          <Tabs defaultValue="details">
            <TabsList className="bg-gray-50 border border-gray-200 mb-6 grid w-full grid-cols-3">
              <TabsTrigger value="details" className="data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
                Org Details
              </TabsTrigger>
              <TabsTrigger value="billing" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                Billing & GST
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
                Users {users !== null ? `(${users.length})` : ""}
              </TabsTrigger>
            </TabsList>

            {/* ── Org Details tab ── */}
            <TabsContent value="details" className="space-y-4">
              <Field label="Logo">
                <OrgLogoField
                  org={org}
                  onChanged={(logo_url) => { onOrgUpdated({ ...org, logo_url }); refreshMyOrg?.(); }}
                />
              </Field>
              <Field label="Organisation Name">
                <Input className="bg-gray-50 border-gray-200" value={details.name}
                  onChange={(e) => setDetails((p) => ({ ...p, name: e.target.value }))} />
              </Field>
              <Field label="Slug (URL key)">
                <Input className="bg-gray-50 border-gray-200 font-mono text-sm" value={details.slug}
                  onChange={(e) => setDetails((p) => ({ ...p, slug: slugify(e.target.value) }))} />
                <p className="text-[11px] text-gray-500 mt-1">Changing slug may break existing login links.</p>
              </Field>
              <Field label="Primary Phone">
                <Input className="bg-gray-50 border-gray-200" value={details.phone}
                  onChange={(e) => setDetails((p) => ({ ...p, phone: e.target.value }))} />
              </Field>
              <Field label="Mobile Numbers (comma-separated)">
                <Input className="bg-gray-50 border-gray-200 text-sm" placeholder="+91-98200-XXXXX, +91-98201-XXXXX" value={details.mobile_numbers}
                  onChange={(e) => setDetails((p) => ({ ...p, mobile_numbers: e.target.value }))} />
              </Field>
              <Button onClick={saveDetails} disabled={savingDetails} className="f360-gradient border-0 w-full">
                {savingDetails ? "Saving…" : "Save Org Details"}
              </Button>

              <div className="pt-4 border-t border-gray-200 space-y-2">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Danger Zone</p>
                {orgStatus === "deleted" ? (
                  <div className="space-y-2">
                    <p className="text-[11px] text-red-600">⚠ Organisation is deleted. All data will be permanently erased.</p>
                    <Button onClick={() => setConfirmDialog({ type: "purge" })} variant="ghost"
                      className="w-full border border-red-300 text-red-600 hover:bg-red-100 font-bold">
                      <Trash2 className="w-4 h-4 mr-1.5" /> Permanently Delete
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <Button onClick={() => setConfirmDialog({ type: "suspend" })} variant="ghost"
                        className={`flex-1 border ${orgStatus === "suspended" ? "border-emerald-200 text-emerald-600 hover:bg-emerald-100" : "border-amber-200 text-amber-600 hover:bg-amber-100"}`}>
                        {orgStatus === "suspended"
                          ? <><PlayCircle className="w-4 h-4 mr-1.5" /> Reactivate Org</>
                          : <><PauseCircle className="w-4 h-4 mr-1.5" /> Suspend Org</>}
                      </Button>
                      <Button onClick={() => setConfirmDialog({ type: "delete" })} variant="ghost"
                        className="flex-1 border border-red-200 text-red-600 hover:bg-red-100">
                        <Trash2 className="w-4 h-4 mr-1.5" /> Delete Org
                      </Button>
                    </div>
                    {orgStatus === "suspended" && (
                      <p className="text-[11px] text-red-600">⚠ Organisation is suspended — all logins blocked.</p>
                    )}
                  </>
                )}
              </div>
            </TabsContent>

            {/* ── Billing & GST tab ── */}
            <TabsContent value="billing" className="space-y-6">
              <div className="space-y-4">
                <div className="text-sm font-bold uppercase tracking-wider text-gray-700 border-b pb-2">GST Details</div>
                <Field label="GST Number">
                  <Input className="bg-gray-50 border-gray-200 font-mono text-sm" placeholder="27AABCT1234H1Z0" value={details.gst_number}
                    onChange={(e) => setDetails((p) => ({ ...p, gst_number: e.target.value })) } />
                </Field>
                <Field label="GST State">
                  <Input className="bg-gray-50 border-gray-200" placeholder="Maharashtra" value={details.gst_state}
                    onChange={(e) => setDetails((p) => ({ ...p, gst_state: e.target.value }))} />
                </Field>
              </div>

              <div className="space-y-4">
                <div className="text-sm font-bold uppercase tracking-wider text-gray-700 border-b pb-2">Billing Details</div>
                <Field label="Billing Contact Person">
                  <Input className="bg-gray-50 border-gray-200" placeholder="John Doe" value={details.billing_contact_person}
                    onChange={(e) => setDetails((p) => ({ ...p, billing_contact_person: e.target.value }))} />
                </Field>
                <Field label="Billing Email">
                  <Input type="email" className="bg-gray-50 border-gray-200" placeholder="billing@company.com" value={details.billing_email}
                    onChange={(e) => setDetails((p) => ({ ...p, billing_email: e.target.value }))} />
                </Field>
                <Field label="Billing Address">
                  <Input className="bg-gray-50 border-gray-200" placeholder="Street address" value={details.billing_address}
                    onChange={(e) => setDetails((p) => ({ ...p, billing_address: e.target.value }))} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="City">
                    <Input className="bg-gray-50 border-gray-200" placeholder="Mumbai" value={details.billing_city}
                      onChange={(e) => setDetails((p) => ({ ...p, billing_city: e.target.value }))} />
                  </Field>
                  <Field label="State">
                    <Input className="bg-gray-50 border-gray-200" placeholder="Maharashtra" value={details.billing_state}
                      onChange={(e) => setDetails((p) => ({ ...p, billing_state: e.target.value }))} />
                  </Field>
                </div>
                <Field label="PIN Code">
                  <Input className="bg-gray-50 border-gray-200" placeholder="400001" value={details.billing_pin}
                    onChange={(e) => setDetails((p) => ({ ...p, billing_pin: e.target.value }))} />
                </Field>
              </div>

              <div className="space-y-4">
                <div className="text-sm font-bold uppercase tracking-wider text-gray-700 border-b pb-2">Shipping Details</div>
                <Field label="Shipping Address">
                  <Input className="bg-gray-50 border-gray-200" placeholder="Street address" value={details.shipping_address}
                    onChange={(e) => setDetails((p) => ({ ...p, shipping_address: e.target.value }))} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="City">
                    <Input className="bg-gray-50 border-gray-200" placeholder="Mumbai" value={details.shipping_city}
                      onChange={(e) => setDetails((p) => ({ ...p, shipping_city: e.target.value }))} />
                  </Field>
                  <Field label="State">
                    <Input className="bg-gray-50 border-gray-200" placeholder="Maharashtra" value={details.shipping_state}
                      onChange={(e) => setDetails((p) => ({ ...p, shipping_state: e.target.value }))} />
                  </Field>
                </div>
                <Field label="PIN Code">
                  <Input className="bg-gray-50 border-gray-200" placeholder="400001" value={details.shipping_pin}
                    onChange={(e) => setDetails((p) => ({ ...p, shipping_pin: e.target.value }))} />
                </Field>
              </div>

              <Button onClick={saveDetails} disabled={savingDetails} className="f360-gradient border-0 w-full">
                {savingDetails ? "Saving…" : "Save Billing & GST Details"}
              </Button>
            </TabsContent>

            {/* ── Users tab ── */}
            <TabsContent value="users">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-600">
                  {users === null ? "Loading…" : `${users.length} user${users.length !== 1 ? "s" : ""}`}
                </h3>
                <Button size="sm" onClick={() => setAddUserOpen(true)} className="f360-gradient border-0 h-8 text-xs gap-1">
                  <UserPlus className="w-3.5 h-3.5" /> Add User
                </Button>
              </div>
              {users === null ? (
                <div className="text-gray-500 text-sm">Loading…</div>
              ) : (
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-[10px] uppercase tracking-widest text-gray-500">
                        <th className="text-left px-4 py-2">Name</th>
                        <th className="text-left px-4 py-2">Email</th>
                        <th className="text-left px-4 py-2">Role</th>
                        <th className="text-right px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <UserRow
                          key={u.user_id}
                          orgId={org.org_id}
                          u={u}
                          onUpdated={(updated) => setUsers((prev) => prev.map((x) => x.user_id === updated.user_id ? updated : x))}
                          onDeleted={(uid) => setUsers((prev) => prev.filter((x) => x.user_id !== uid))}
                          onResetPw={setResetTarget}
                          onSuspendToggle={suspendUser}
                        />
                      ))}
                      {users.length === 0 && (
                        <tr><td colSpan={4} className="text-center py-6 text-gray-500">No users in this org.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {resetTarget && (
        <ResetPasswordDialog orgId={org.org_id} target={resetTarget} onClose={() => setResetTarget(null)} />
      )}
      {addUserOpen && (
        <AddUserDialog
          orgId={org.org_id}
          onClose={() => setAddUserOpen(false)}
          onCreated={(newUser) => setUsers((prev) => [...(prev || []), newUser])}
        />
      )}
      {confirmDialog?.type === "suspend" && (
        <ConfirmSlugDialog
          danger={false}
          title={orgStatus === "suspended" ? "Reactivate Organisation" : "Suspend Organisation"}
          description={orgStatus === "suspended"
            ? `Reactivating will restore login access for all users of "${org.name}".`
            : `Suspending will immediately block all users of "${org.name}" from logging in.`}
          slug={org.slug}
          confirmLabel={orgStatus === "suspended" ? "Reactivate" : "Suspend"}
          onConfirm={doSuspendToggle}
          onClose={() => setConfirmDialog(null)}
        />
      )}
      {confirmDialog?.type === "delete" && (
        <ConfirmSlugDialog
          danger
          title="Delete Organisation"
          description={`This will soft-delete "${org.name}" and block all its users from logging in. You can permanently erase it afterwards.`}
          slug={org.slug}
          confirmLabel="Delete Organisation"
          onConfirm={doDeleteOrg}
          onClose={() => setConfirmDialog(null)}
        />
      )}
      {confirmDialog?.type === "purge" && (
        <ConfirmSlugDialog
          danger
          title="Permanently Delete Organisation"
          description={`All data for "${org.name}" — users, sites, subscriptions — will be erased from the database forever. This cannot be undone.`}
          slug={org.slug}
          confirmLabel="Permanently Delete"
          onConfirm={doPurgeOrg}
          onClose={() => setConfirmDialog(null)}
        />
      )}
    </>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
const EMPTY_FORM = { org_name: "", org_slug: "", admin_name: "", admin_email: "", admin_password: "", phone: "" };

export default function AdminOrganizations() {
  const [orgs, setOrgs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [created, setCreated]       = useState(null);
  const [editOrg, setEditOrg]       = useState(null);
  const [deletedOrgs, setDeletedOrgs]     = useState([]);
  const [showDeleted, setShowDeleted]     = useState(false);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/orgs")
      .then((r) => setOrgs(r.data))
      .catch(() => toast.error("Failed to load organisations"))
      .finally(() => setLoading(false));
  };

  const loadDeleted = () => {
    setLoadingDeleted(true);
    api.get("/admin/orgs?include_deleted=true")
      .then((r) => setDeletedOrgs(r.data.filter((o) => o.status === "deleted")))
      .catch(() => toast.error("Failed to load deleted organisations"))
      .finally(() => setLoadingDeleted(false));
  };

  const toggleShowDeleted = () => {
    if (!showDeleted && deletedOrgs.length === 0) loadDeleted();
    setShowDeleted((v) => !v);
  };

  useEffect(() => { load(); }, []);

  const f = (k) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({
      ...prev,
      [k]: val,
      ...(k === "org_name" && !prev._slug_edited ? { org_slug: slugify(val) } : {}),
    }));
  };

  const submit = async () => {
    if (!form.org_name || !form.admin_email || !form.admin_password || !form.admin_name || !form.phone.trim()) {
      toast.error("Organisation name, admin name, email, mobile number and password are required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/tenant/register", {
        org_name:       form.org_name,
        org_slug:       form.org_slug || slugify(form.org_name),
        admin_name:     form.admin_name,
        admin_email:    form.admin_email,
        admin_password: form.admin_password,
        phone:          form.phone,
      });
      setCreated({
        org_name:      form.org_name,
        admin_email:   form.admin_email,
        temp_password: form.admin_password,
        org_id:        r.data.org?.org_id,
      });
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create organisation");
    } finally {
      setSaving(false);
    }
  };

  const total      = orgs.length;
  const active     = orgs.filter((o) => o.sub_status === "active").length;
  const trial      = orgs.filter((o) => o.sub_status === "trial").length;
  const enterprise = orgs.filter((o) => o.plan_tier === "enterprise").length;

  const [planBusy, setPlanBusy] = useState(null);
  // The plan sets how many sites/users an org may have — every new org starts on basic (1 site).
  const setOrgPlan = async (org, plan_tier) => {
    if (plan_tier === (org.plan_tier || "basic")) return;
    if (!window.confirm(`Change ${org.name} to the ${plan_tier} plan?`)) return;
    setPlanBusy(org.org_id);
    try {
      await api.post(`/admin/orgs/${org.org_id}/plan`, { plan_tier });
      toast.success(`${org.name} is now on the ${plan_tier} plan`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change plan");
    } finally { setPlanBusy(null); }
  };

  const [statusBusy, setStatusBusy] = useState(null);
  const setOrgStatus = async (e, org, status) => {
    e.stopPropagation(); // the row itself opens the org drawer
    const verb = status === "active" ? "Activate" : "Move back to trial";
    if (!window.confirm(`${verb}: ${org.name}?`)) return;
    setStatusBusy(org.org_id);
    try {
      await api.post(`/admin/orgs/${org.org_id}/subscription-status`, { status });
      toast.success(status === "active" ? `${org.name} is now active` : `${org.name} moved to trial`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change status");
    } finally { setStatusBusy(null); }
  };

  const activateAllOrgs = async () => {
    if (!window.confirm("Activate all organisations? This will set all subscriptions to active status.")) return;
    try {
      await api.post("/admin/activate-all-orgs");
      toast.success("All organisations activated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate organisations");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-orgs-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Shield className="w-3 h-3" strokeWidth={2.5} /> Platform Admin
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Organisations</h1>
          <p className="text-sm text-gray-500 mt-1">All tenants registered on this platform</p>
        </div>
        <div className="flex items-center gap-2">
          {trial > 0 && (
            <Button onClick={activateAllOrgs} variant="ghost" className="border border-amber-200 text-amber-700 hover:bg-amber-50">
              Activate All ({trial})
            </Button>
          )}
          <Button onClick={() => { setCreated(null); setCreateOpen(true); }} className="f360-gradient border-0">
            <Plus className="w-4 h-4 mr-1" /> Create Organisation
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total Orgs"   value={total}      icon={Building2} />
        <Kpi label="Active"       value={active}     icon={CheckCircle2} tone="text-emerald-600" />
        <Kpi label="Trial"        value={trial}      icon={Globe}        tone="text-amber-600" />
        <Kpi label="Enterprise"   value={enterprise} icon={Shield}       tone="text-purple-400" />
      </div>

      {/* Table */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-[10px] uppercase tracking-widest text-gray-500">
                <th className="text-left px-4 py-3">Organisation</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-center px-4 py-3">Sites</th>
                <th className="text-center px-4 py-3">Users</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-10 text-gray-500">Loading…</td></tr>
              )}
              {!loading && orgs.map((o) => (
                <tr
                  key={o.org_id}
                  className="border-t border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setEditOrg(o)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full f360-gradient flex items-center justify-center text-xs font-bold text-white shrink-0">
                        {o.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold">{o.name}</div>
                        <div className="text-[10px] text-gray-500 font-mono">{o.org_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5">
                        <Badge className={`border text-[10px] uppercase tracking-widest ${STATUS_COLORS[o.sub_status] || STATUS_COLORS.trial}`}>
                          {o.sub_status}
                        </Badge>
                        {o.sub_status === "trial" ? (
                          <button
                            type="button"
                            onClick={(e) => setOrgStatus(e, o, "active")}
                            disabled={statusBusy === o.org_id}
                            className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                            data-testid={`activate-org-${o.org_id}`}
                          >
                            {statusBusy === o.org_id ? "…" : "Activate"}
                          </button>
                        ) : o.sub_status === "active" ? (
                          <button
                            type="button"
                            onClick={(e) => setOrgStatus(e, o, "trial")}
                            disabled={statusBusy === o.org_id}
                            className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-gray-400 hover:text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                            title="Move back to trial"
                            data-testid={`trial-org-${o.org_id}`}
                          >
                            → trial
                          </button>
                        ) : null}
                      </div>
                      {o.status === "suspended" && (
                        <Badge className="border text-[10px] uppercase tracking-widest bg-red-50 text-red-700 border-red-200">
                          suspended
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={o.plan_tier || "basic"}
                      onChange={(e) => setOrgPlan(o, e.target.value)}
                      disabled={planBusy === o.org_id}
                      className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white disabled:opacity-50"
                      data-testid={`plan-org-${o.org_id}`}
                    >
                      {PLAN_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-center font-bold">{o.site_count}</td>
                  <td className="px-4 py-3 text-center font-bold">{o.user_count}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {o.created_at ? new Date(o.created_at).toLocaleDateString("en-IN") : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-gray-500 inline-block" />
                  </td>
                </tr>
              ))}
              {!loading && orgs.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-gray-500">No organisations yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deleted organisations section */}
      <div className="glass rounded-2xl overflow-hidden">
        <button
          onClick={toggleShowDeleted}
          className="w-full flex items-center justify-between px-5 py-3 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold uppercase tracking-widest text-[11px]">
            <Trash2 className="w-3.5 h-3.5 text-red-600" />
            Deleted Organisations
            {deletedOrgs.length > 0 && (
              <span className="bg-red-50 text-red-600 text-[10px] px-2 py-0.5 rounded-full border border-red-200">
                {deletedOrgs.length}
              </span>
            )}
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showDeleted ? "rotate-180" : ""}`} />
        </button>

        {showDeleted && (
          <div className="border-t border-gray-200">
            {loadingDeleted ? (
              <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
            ) : deletedOrgs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">No deleted organisations.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-gray-500 bg-red-50/50">
                    <th className="text-left px-4 py-2">Organisation</th>
                    <th className="text-left px-4 py-2">Deleted At</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {deletedOrgs.map((o) => (
                    <tr key={o.org_id}
                      className="border-t border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer opacity-60 hover:opacity-100"
                      onClick={() => setEditOrg(o)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 shrink-0">
                            {o.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold line-through text-gray-500">{o.name}</div>
                            <div className="text-[10px] text-gray-600 font-mono">{o.org_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {o.deleted_at ? new Date(o.deleted_at).toLocaleString("en-IN") : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-[10px] text-red-600 font-bold uppercase tracking-widest">Purge →</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Create org dialog */}
      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) { setCreated(null); setForm(EMPTY_FORM); } }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Organisation</DialogTitle>
            <p className="text-xs text-gray-600 mt-1">
              Creates a new tenant org and an <strong className="text-orange-700">Org Admin</strong> account.
              Share the credentials with the client — they can log in and manage their org independently.
            </p>
          </DialogHeader>

          {created ? (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                <div className="text-xs text-emerald-700 font-bold uppercase tracking-wider mb-1">Organisation created</div>
                <div className="text-sm">Org: <span className="font-semibold">{created.org_name}</span></div>
                <div className="text-sm">Admin email: <span className="font-mono">{created.admin_email}</span></div>
                <div className="text-sm flex items-center gap-2">
                  Password: <span className="font-mono text-orange-700">{created.temp_password}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-2"
                    onClick={() => { navigator.clipboard?.writeText(created.temp_password); toast.success("Copied"); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                {created.org_id && (
                  <div className="text-[11px] text-gray-600 font-mono">{created.org_id}</div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => { setCreateOpen(false); setCreated(null); }} className="f360-gradient border-0">Done</Button>
                <Button variant="ghost" onClick={() => { setCreated(null); setForm(EMPTY_FORM); }}>Create Another</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Organisation Name *</Label>
                    <Input className="bg-gray-50 border-gray-200 mt-1" value={form.org_name}
                      onChange={f("org_name")} placeholder="TATA Memorial Hospital" />
                  </div>
                  <div>
                    <Label>Slug (URL key)</Label>
                    <Input className="bg-gray-50 border-gray-200 mt-1 font-mono text-sm" value={form.org_slug}
                      onChange={(e) => setForm({ ...form, org_slug: slugify(e.target.value), _slug_edited: true })}
                      placeholder="tata-memorial" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Admin Name *</Label>
                    <Input className="bg-gray-50 border-gray-200 mt-1" value={form.admin_name}
                      onChange={f("admin_name")} placeholder="Dr. Suresh Nair" />
                  </div>
                  <div>
                    <Label>Admin Mobile *</Label>
                    <Input type="tel" inputMode="tel" className="bg-gray-50 border-gray-200 mt-1" value={form.phone}
                      onChange={f("phone")} placeholder="98200 12345" />
                  </div>
                </div>
                <div>
                  <Label>Admin Email *</Label>
                  <Input type="email" className="bg-gray-50 border-gray-200 mt-1" value={form.admin_email}
                    onChange={f("admin_email")} placeholder="admin@hospital.com" />
                </div>
                <div>
                  <Label>Initial Password *</Label>
                  <Input type="text" className="bg-gray-50 border-gray-200 mt-1 font-mono" value={form.admin_password}
                    onChange={f("admin_password")} placeholder="Minimum 8 characters" />
                  <p className="text-[11px] text-gray-500 mt-1">Share this with the client. They can change it after first login.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={saving} className="f360-gradient border-0">
                  {saving ? "Creating…" : "Create Organisation"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit org drawer */}
      {editOrg && (
        <EditOrgDrawer
          org={editOrg}
          onClose={() => setEditOrg(null)}
          onOrgUpdated={(updated) => {
            setOrgs((prev) => prev.map((o) => o.org_id === updated.org_id ? { ...o, ...updated } : o));
            setEditOrg((prev) => ({ ...prev, ...updated }));
          }}
          onOrgDeleted={(orgId) => {
            // Soft-delete: remove from active list, add to deleted list
            const org = orgs.find((o) => o.org_id === orgId);
            setOrgs((prev) => prev.filter((o) => o.org_id !== orgId));
            if (org) setDeletedOrgs((prev) => [{ ...org, status: "deleted", deleted_at: new Date().toISOString() }, ...prev]);
            setEditOrg(null);
          }}
          onOrgPurged={(orgId) => {
            setDeletedOrgs((prev) => prev.filter((o) => o.org_id !== orgId));
            setEditOrg(null);
          }}
        />
      )}
    </div>
  );
}
